/**
 * Bloco E (mig 379) — Home do ORÇAMENTISTA.
 *
 * Servida a quem é do departamento 'orcamentista' (HomePage decide). NÃO é um
 * funil paralelo: é a MESMA fila de cards, filtrada pelas fases que EXIGEM data
 * de entrega de orçamento e que ainda não foram concluídas. O card NUNCA muda de
 * responsável — cada linha mostra o VENDEDOR dono e leva ao card no funil.
 *
 * Ordenação (FILA-NOITE 05/08): pela DATA DA PROPOSTA prometida ao cliente
 * (sem data → fim da fila); empate desempata pela entrega interna. Copy
 * pt-BR, sem jargão, sem emoji.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';

import { EmptyState, HelpTip, PageHeader } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/cn';
import { formatAgendamento, formatarData, greeting } from '@/lib/format-date';
import { classesChipUrgencia, pesoUrgencia } from '@/lib/crm/entrega-orcamento';
import { useCrmCards, useFases } from '@/lib/crm/crm-store';
// CA-10: botão de 3 estados do orçamentista — o MESMO slot do drawer do card.
import { BotaoCronometroOrcamento } from '@/pages/comercial/crm-shared';
import { MinhaAgendaSection } from './MinhaAgendaSection';
// CARD-VENDAS-VENDEDOR (21/08): mecanismo geral — orçamentista vinculado a um
// vendedor do Omie também ganha o card; sem vínculo o widget é null.
import { MinhasVendasWidget } from './widgets/MinhasVendasWidget';

export function HomeOrcamentista() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: cards = [] } = useCrmCards({});
  const { data: fases = [] } = useFases();

  const fasesGatilho = useMemo(
    () => new Set(fases.filter((f) => f.exigeDataEntrega).map((f) => f.id)),
    [fases],
  );

  // FILA-NOITE (05/08): a fila é a MESMA definição do funil (Regra 12 — uma
  // definição só): card ATIVO, não fechado, numa fase-gatilho, orçamento não
  // concluído. O `&& c.entregaOrcamento` antigo era uma SEGUNDA definição
  // divergente: card criado direto na fase-gatilho (o POST não passa pela
  // trava do mover) nasce SEM prazo de entrega e ficava INVISÍVEL aqui
  // enquanto o funil o mostrava — caso real #1044. Sem prazo agora aparece,
  // dizendo que está sem prazo.
  /**
   * 🔴🔴 **A CHAVE DA FILA — e ela MUDOU em 27/09/2026 (B5).**
   *
   * ⛔ **Era a DATA DA PROPOSTA** (*"o prazo prometido ao cliente é como ele
   * prioriza"*, decisão do Matheus em 05/08). ✅ **O campo saiu inteiro** —
   * *"pode excluir essa data da proposta, remova a função"* (27/09).
   *
   * 🔴 **A chave nova entrou ANTES de a antiga sair**, e isso é requisito, não
   * capricho: a fila é de alguém que trabalha nela todo dia, e **se a rodada
   * parasse no meio ela não podia ficar sem critério**.
   *
   * ✅ **A nova é a ENTREGA DO ORÇAMENTO — o prazo interno — e a medição a
   * escolheu:** dos **16** cards na fila do beta, **16 têm prazo de entrega** e
   * só **1** tinha data de proposta. ⚠️ Na prática a fila antiga caía no ramo
   * *"sem data vai pro fim"* em 15 dos 16 — **ordenava por acaso.**
   *
   * **A ordem, em três níveis:**
   *  1. **urgência** da entrega (atrasado → hoje → amanhã → folga);
   *  2. a **data** da entrega, dentro da mesma urgência;
   *  3. 🔴 **`criadoEm` — ordem de chegada, que NUNCA é nula.** ⚠️ Card criado
   *     direto na fase-gatilho nasce sem prazo (caso #1044) e cairia num
   *     empate sem desempate: dois cards sem prazo ficariam em ordem
   *     indefinida, **que muda a cada render**.
   */
  const fila = useMemo(
    () =>
      cards
        .filter((c) => c.ativo && !c.fechadoEm && fasesGatilho.has(c.faseId) && !c.orcamentoConcluidoEm)
        .sort((a, b) => {
          const pa = a.entregaOrcamento ? pesoUrgencia(a.entregaOrcamento.urgencia) : 99;
          const pb = b.entregaOrcamento ? pesoUrgencia(b.entregaOrcamento.urgencia) : 99;
          if (pa !== pb) return pa - pb;
          const da = a.entregaOrcamento?.data ?? '9999';
          const db = b.entregaOrcamento?.data ?? '9999';
          if (da !== db) return da.localeCompare(db);
          return (a.criadoEm ?? '').localeCompare(b.criadoEm ?? '');
        }),
    [cards, fasesGatilho],
  );

  const atrasados = fila.filter((c) => c.entregaOrcamento?.urgencia === 'atrasado').length;
  const hoje = fila.filter((c) => c.entregaOrcamento?.urgencia === 'hoje').length;

  // #484: a data e a saudação seguem o relógio de Brasília, não o do computador.
  const dataLonga = formatarData(new Date(), { weekday: 'long', day: 'numeric', month: 'long' }).replace(
    /^./,
    (c) => c.toUpperCase(),
  );
  const saudacao = greeting();

  return (
    <div className="space-y-6">
      {/* FASE-C: PageHeader canônico — a data vira o crumb, a saudação é o h1. */}
      <PageHeader
        crumb={dataLonga}
        title={`${saudacao}, ${user?.name?.split(' ')[0] ?? ''}`}
        className="mb-0"
      />

      {/* CARD-VENDAS-VENDEDOR: null pra quem não tem vínculo com o Omie. */}
      <MinhasVendasWidget />

      <section>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3 inline-flex items-center gap-1.5">
            Orçamentos a fazer
            {fila.length > 0 ? ` · ${fila.length}` : ''}
            <HelpTip texto="A fila de propostas aguardando orçamento — a MESMA que você vê no funil — ordenada pelo prazo de entrega: os atrasados primeiro, depois os de hoje, e assim por diante. Dentro do mesmo prazo, quem chegou antes vem antes. O chip mostra quantos dias úteis faltam ou passaram. O responsável pela proposta continua sendo o vendedor — aqui você só monta o orçamento." />
          </p>
          {(atrasados > 0 || hoje > 0) && (
            <p className="text-xs font-semibold text-red dark:text-red-glow">
              {atrasados > 0 ? `${atrasados} atrasado${atrasados === 1 ? '' : 's'}` : ''}
              {atrasados > 0 && hoje > 0 ? ' · ' : ''}
              {hoje > 0 ? `${hoje} para hoje` : ''}
            </p>
          )}
        </div>

        <div className="bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-xl shadow-card">
          {fila.length === 0 ? (
            <EmptyState
              icon={<ClipboardCheck className="w-6 h-6" aria-hidden />}
              title="Nenhuma proposta aguardando orçamento"
              description="Assim que uma proposta entrar na fase de orçamento, ela aparece aqui, ordenada pelo prazo de entrega."
            />
          ) : (
            fila.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-line-soft dark:border-line-soft-dark last:border-b-0"
              >
                <button
                  type="button"
                  data-testid={`orcamentista-fila-${c.id}`}
                  onClick={() => navigate(`/admin/comercial/funil?abrirCard=${encodeURIComponent(c.id)}`)}
                  /**
                   * 🔴 **EM 375px O NOME FICAVA EM UMA LETRA** — "P…", "C…",
                   * "Q…" —, achado no print desta entrega. ⚠️ Com todas as
                   * linhas assim, o Kevyn não distingue uma proposta da outra,
                   * e a fila inteira vira uma lista de reticências.
                   *
                   * **A causa:** o chip de prazo e o "Abrir no funil" não
                   * encolhem, então quem cedia largura era sempre o nome.
                   * ✅ **A cura é a mesma do #440:** no celular o nome fica com
                   * a linha inteira e o resto desce; de `sm` para cima, nada
                   * muda.
                   */
                  className="basis-full min-w-0 sm:basis-0 sm:flex-1 sm:min-w-[200px] flex flex-wrap items-center gap-x-3 gap-y-1 text-left group"
                >
                  {/* 🔴 No celular o NOME fica com a linha inteira e o chip
                      desce; de `sm` para cima, nada muda. ⚠️ Sem `basis-full`
                      aqui, o nome cedia largura ao chip e ao link — que não
                      encolhem — e virava UMA LETRA ("P…", "C…"). */}
                  <span className="basis-full min-w-0 sm:basis-0 sm:flex-1">
                    <span className="block text-sm font-semibold text-ink-1 dark:text-ink-dark-1 group-hover:text-red truncate">
                      {c.displayNome}
                    </span>
                    <span className="block text-xs text-ink-3 dark:text-ink-dark-3 truncate">
                      {c.servicoNome ?? 'Sem serviço'} · Vendedor: {(c.responsavelNome ?? 'sem responsável')}
                    </span>
                    {/* ⛔ B5 (27/09/2026) — A LINHA "Proposta ao cliente" SAIU.
                        Ela nasceu em 05/08 porque a data prometida ao cliente
                        não chegava ao orçamentista em lugar nenhum; o campo
                        inteiro foi removido por decisão do Matheus.

                        ✅ O que responde "qual é o prazo" continua aqui ao
                        lado: o chip de urgência da ENTREGA, que é o prazo
                        interno — e é ele que ordena a fila agora. */}
                  </span>
                  {c.entregaOrcamento ? (
                    <>
                      <span
                        className={cn(
                          'text-xs font-semibold rounded-pill px-2 py-0.5 shrink-0',
                          classesChipUrgencia(c.entregaOrcamento.urgencia),
                        )}
                      >
                        {c.entregaOrcamento.label}
                      </span>
                      <span className="font-mono text-xs tabular-nums text-ink-3 dark:text-ink-dark-3 shrink-0 hidden sm:block">
                        {formatAgendamento(c.entregaOrcamento.data)}
                      </span>
                    </>
                  ) : (
                    /* FILA-NOITE: card que NASCEU na fase-gatilho (o POST não
                       passa pela trava do mover) não tem prazo interno — a
                       linha diz isso em vez de sumir da fila (#1044). */
                    <span
                      data-testid={`orcamentista-sem-prazo-${c.id}`}
                      className="text-xs font-semibold rounded-pill px-2 py-0.5 shrink-0 bg-line-soft dark:bg-line-soft-dark text-ink-2 dark:text-ink-dark-2"
                    >
                      sem prazo de entrega
                    </span>
                  )}
                  <span aria-hidden className="text-ink-3 dark:text-ink-dark-3 shrink-0">
                    Abrir no funil ›
                  </span>
                </button>
                {/* CA-10: o botão do cronômetro no MESMO slot da fila. Depois de
                    "Fazer orçamento", abre o card no funil (aba Orçamento). */}
                <BotaoCronometroOrcamento
                  cardId={c.id}
                  aoIniciarProducao={() =>
                    navigate(`/admin/comercial/funil?abrirCard=${encodeURIComponent(c.id)}`)
                  }
                />
              </div>
            ))
          )}
        </div>
      </section>

      {/* 07/08/2026 — "Minha agenda" no orçamentista (decisão do Matheus). É a
          ÚNICA mudança nesta tela: nada saiu, nada foi redesenhado. Reusa o
          motor escopado por pessoa do #78, o mesmo da HomeComercial. */}
      <MinhaAgendaSection />
    </div>
  );
}
